<template>
  <AppLayout>
    <div class="flex flex-col items-center gap-4">
      <h1>Chat Agent</h1>

      <div class="flex flex-col gap-4 w-full max-w-4xl">
        <p class="text-sm text-surface-500">
          System prompt for the onboarding chat agent. Saved changes apply on the next chat
          message — no deploy needed.
        </p>

        <Textarea
          v-model="systemPrompt"
          :disabled="loading"
          class="w-full font-mono text-sm"
          style="height: 60vh"
          placeholder="Loading..."
        />

        <div class="flex items-center gap-4">
          <Button
            label="Save"
            :loading="saving"
            :disabled="loading || !isDirty || !systemPrompt.trim()"
            @click="save"
          />
          <span v-if="isDirty" class="text-sm text-amber-500">Unsaved changes</span>
          <span v-else-if="modified" class="text-sm text-surface-400">
            Last saved {{ formatDate(modified) }}
          </span>
          <span v-else-if="!loading" class="text-sm text-surface-400 italic">
            Built-in default (never saved)
          </span>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import dayjs from 'dayjs';
import AppLayout from '@/components/AppLayout.vue';
import { useApiClient } from '@/api/apiClient';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';

const { api } = useApiClient();
const toast = useToast();

const systemPrompt = ref('');
const savedPrompt = ref('');
const modified = ref<number | null>(null);
const loading = ref(true);
const saving = ref(false);

const isDirty = computed(() => systemPrompt.value !== savedPrompt.value);

function formatDate(date: number): string {
  return dayjs(date).format('MMM D, YYYY HH:mm');
}

async function load() {
  loading.value = true;
  try {
    const { data, error } = await api.adminChatConfigControllerGet();
    if (error || !data) {
      throw new Error('Failed to load chat agent config');
    }
    systemPrompt.value = data.systemPrompt;
    savedPrompt.value = data.systemPrompt;
    modified.value = data.modified;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Failed to load chat agent config', life: 5000 });
    throw e;
  } finally {
    loading.value = false;
  }
}

async function save() {
  saving.value = true;
  try {
    const { data, error } = await api.adminChatConfigControllerUpdate({
      body: { systemPrompt: systemPrompt.value },
    });
    if (error || !data) {
      throw new Error('Failed to save chat agent config');
    }
    systemPrompt.value = data.systemPrompt;
    savedPrompt.value = data.systemPrompt;
    modified.value = data.modified;
    toast.add({ severity: 'success', summary: 'Chat agent config saved', life: 3000 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Failed to save chat agent config', life: 5000 });
    throw e;
  } finally {
    saving.value = false;
  }
}

load();
</script>
